import React, { FC } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { SalaryCostData } from "../../hooks";

import { useGetPaygapSettings } from "@api/hooks/paygap";
import { useUserStore } from "@store/user";
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@components/base";
import { DonutChart } from "@components/common/charts";

interface PaygapSalaryCostAnalysisProps {
  salaryCostData: SalaryCostData;
}

const PaygapSalaryCostAnalysis: FC<PaygapSalaryCostAnalysisProps> = ({
  salaryCostData,
}) => {
  const { t } = useTranslation();

  // Get user for API hook
  const { user } = useUserStore();
  const { settings } = useGetPaygapSettings(user, true);

  // Resolve currency code from API settings
  const currencyCode = settings?.analysisParameters?.currency || "EUR";

  // Helper function to format values
  const formatValue = (
    value: string,
    isPercentage: boolean = false,
    isCurrency: boolean = false,
  ): string => {
    if (
      value === "#DIV/0!" ||
      value === "#VALUE!" ||
      value === "#N/A" ||
      !value ||
      value === "null"
    )
      return "N/A";
    const num = parseFloat(value);

    if (isNaN(num)) return "N/A";

    if (isCurrency) {
      try {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(num);
      } catch {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(num);
      }
    }

    if (isPercentage) {
      return `${num.toFixed(1)}%`;
    }

    return num.toLocaleString();
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t("paygap.analysis.salaryCost.title")}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("paygap.analysis.salaryCost.gender")}</TableCell>
                  <TableCell align="right">
                    {t("paygap.analysis.salaryCost.totalCost")}
                  </TableCell>
                  <TableCell align="right">
                    {t("paygap.analysis.salaryCost.percentage")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{t("paygap.analysis.salaryCost.male")}</TableCell>
                  <TableCell align="right">
                    {formatValue(
                      salaryCostData.men.totalCost.toString(),
                      false,
                      true,
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {formatValue(salaryCostData.men.sharePercent.toString(), true)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t("paygap.analysis.salaryCost.female")}</TableCell>
                  <TableCell align="right">
                    {formatValue(
                      salaryCostData.women.totalCost.toString(),
                      false,
                      true,
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {formatValue(salaryCostData.women.sharePercent.toString(), true)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>{t("paygap.analysis.salaryCost.total")}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>
                      {formatValue(
                        salaryCostData.total.totalCost.toString(),
                        false,
                        true,
                      )}
                    </strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>
                      {formatValue(
                        salaryCostData.total.sharePercent.toString(),
                        true,
                      )}
                    </strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box display="flex" justifyContent="center">
              <DonutChart
                data={[
                  {
                    label: `${t("paygap.graph.men")}: ${formatValue(salaryCostData.men.sharePercent.toString(), true)} (${new Intl.NumberFormat("en-US", { style: "currency", currency: currencyCode, maximumFractionDigits: 0 }).format(salaryCostData.men.totalCost / 1000)}k): ${formatValue(salaryCostData.men.sharePercent.toString(), true)}`,
                    value: salaryCostData.men.sharePercent,
                    color: "#2196f3",
                  },
                  {
                    label: `${t("paygap.graph.women")}: ${formatValue(salaryCostData.women.sharePercent.toString(), true)} (${new Intl.NumberFormat("en-US", { style: "currency", currency: currencyCode, maximumFractionDigits: 0 }).format(salaryCostData.women.totalCost / 1000)}k): ${formatValue(salaryCostData.women.sharePercent.toString(), true)}`,
                    value: salaryCostData.women.sharePercent,
                    color: "#9c27b0",
                  },
                ]}
                title={`${t("paygap.analysis.salaryCost.total")} ${new Intl.NumberFormat("en-US", { style: "currency", currency: currencyCode, maximumFractionDigits: 1 }).format(salaryCostData.total.totalCost / 1000000)}M`}
                size={130}
                strokeWidth={15}
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PaygapSalaryCostAnalysis;
